<template>
  <section class="overview-panel">
    <article class="mobile-overview-asset" aria-label="我的资产总览">
      <div class="mobile-overview-asset__head">
        <span>总资产 (USDT)</span>
        <em><i />LIVE</em>
      </div>
      <strong class="mobile-overview-asset__total">{{ money(balances.total) }}</strong>
      <small>≈ ¥ {{ convertedTotal }}</small>
      <div class="mobile-overview-asset__profits">
        <div>
          <span>今日收益</span>
          <strong>{{ signed(earnings.todayProfit) }}</strong>
        </div>
        <i />
        <div>
          <span>累计收益</span>
          <strong>{{ signed(earnings.profit) }}</strong>
        </div>
      </div>
      <div class="mobile-overview-asset__actions">
        <button type="button" @click="$emit('action', 'deposit')">
          <i class="el-icon-download" />
          <span>充值</span>
        </button>
        <button type="button" @click="$emit('action', 'withdraw')">
          <i class="el-icon-upload2" />
          <span>提现</span>
        </button>
        <button type="button" @click="$emit('action', 'transfer')">
          <i class="el-icon-sort" />
          <span>划转</span>
        </button>
      </div>
    </article>

    <article class="overview-banner">
      <div>
        <div class="overview-banner__identity">
          <h1>{{ greeting }}，{{ userName }}</h1>
          <span class="pro-badge">PRO</span>
          <span class="risk-status"><i />{{ syncStatus }}</span>
        </div>
        <p>{{ syncTime }} · 全部交易所</p>
      </div>
      <div class="overview-banner__right">
        <div class="overview-banner__today">
          <span><i /> LIVE　今日收益 (USDT)</span>
          <strong>{{ signed(earnings.todayProfit) }}</strong>
        </div>
        <i class="overview-banner__divider" />
        <div class="overview-banner__actions">
          <button type="button" @click="$emit('navigate', 'earnings')">查看收益明细</button>
          <button type="button" @click="$emit('action', 'transfer')">账户划转</button>
          <button class="running" type="button" @click="$emit('navigate', 'robots')">
            <i class="el-icon-s-data" /> 机器人运行中
          </button>
        </div>
      </div>
    </article>

    <article class="up-card overview-stats">
      <div>
        <span>总资产 (USDT)</span>
        <strong>{{ money(balances.total) }}</strong>
        <small>≈ ¥ {{ convertedTotal }}</small>
      </div>
      <div>
        <span>今日收益 (USDT)</span>
        <strong class="positive">{{ signed(earnings.todayProfit) }}</strong>
        <small><b>{{ rateText(earnings.todayYield, true) }}</b> 日收益率</small>
      </div>
      <div>
        <span>累计收益 (USDT)</span>
        <strong class="positive">{{ signed(earnings.profit) }}</strong>
        <small>累计收益率 <em>{{ rateText(earnings.yield, true) }}</em></small>
      </div>
      <div>
        <span>运行中机器人</span>
        <strong>{{ runningCount }} <i>台</i></strong>
        <small>{{ healthSummary }}</small>
      </div>
    </article>

    <div class="overview-dashboard-grid">
      <div class="overview-dashboard__main">
        <article class="up-card earnings-chart">
        <div class="panel-heading">
          <h2>收益趋势</h2>
          <div class="earnings-period-selects">
            <select
              :value="period.year"
              :disabled="periodLoading"
              aria-label="选择收益年份"
              @change="changeYear"
            >
              <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}年</option>
            </select>
            <select
              :value="period.month"
              :disabled="periodLoading"
              aria-label="选择收益月份"
              @change="changeMonth"
            >
              <option
                v-for="month in monthOptions"
                :key="month.value"
                :value="month.value"
                :disabled="month.disabled"
              >
                {{ month.value }}月
              </option>
            </select>
          </div>
        </div>
        <div class="earnings-chart__numbers">
          <div><span>累计收益 (USDT)</span><strong :class="periodAmountClass">{{ periodAmount }}</strong></div>
          <div><span>累计收益率</span><strong>{{ periodRate }}</strong></div>
        </div>
        <div class="earnings-chart__canvas">
          <EChartLine
            v-if="chartPoints.length"
            :points="chartPoints"
            :labels="chartLabels"
            :tooltip-rates="chartRates"
            tooltip-mode="overview"
            aria-label="账户累计收益趋势"
          />
          <div v-else class="panel-empty">暂无收益趋势数据</div>
        </div>
        <div v-if="chartLabels.length" class="earnings-chart__axis">
          <span v-for="label in axisLabels" :key="label">{{ label }}</span>
        </div>
        </article>

        <article class="up-card strategy-table">
          <div class="strategy-table__heading">
            <h2>运行中策略</h2>
            <button type="button" @click="$emit('navigate', 'robots')">前往机器人页 ›</button>
          </div>
          <div class="strategy-table__head">
            <span>策略名称</span><span>交易对</span><span>交易所</span><span>今日收益</span><span>保证金健康度</span><span>风险</span><span>操作</span>
          </div>
          <div class="strategy-table__scroll">
            <div v-for="robot in robots" :key="robot.id" class="strategy-table__row">
              <strong>{{ robot.name }}</strong>
              <span>{{ robot.symbol }}</span>
              <span>{{ robot.exchange }}</span>
              <strong class="positive">--</strong>
              <span v-if="robot.health !== null" class="health"><i><b :style="{ width: `${robot.health}%` }" /></i>{{ robot.health }}%</span>
              <span v-else>--</span>
              <span v-if="robot.health !== null" class="risk" :class="{ warning: robot.health < 80 }">{{ robot.health < 80 ? '关注' : '良好' }}</span>
              <span v-else>--</span>
              <button type="button" @click="$emit('robot', robot)">管理</button>
            </div>
            <div v-if="!robots.length" class="panel-empty">暂无策略数据</div>
          </div>
        </article>
      </div>

      <div class="overview-dashboard__side">
        <article class="weekly-board">
        <div class="weekly-board__head">
          <div>
            <span class="weekly-board__crown" aria-hidden="true">
              <img src="@/assets/rebuild/weekly-board-crown-white.svg" alt="">
            </span>
            <h2>本周收益榜</h2>
          </div>
          <span><i />{{ weekRange }}</span>
        </div>
        <div class="weekly-board__list">
          <div
            v-for="leader in visibleLeaders"
            :key="leader.rank"
            class="weekly-board__row"
            :class="{ mine: leader.mine }"
          >
            <span class="rank" :class="`rank-${leader.rank}`">{{ leader.rank }}</span>
            <strong>{{ leader.name }}</strong>
            <img
              v-if="leader.rank === 1"
              class="crown"
              src="@/assets/rebuild/weekly-board-crown-gold.svg"
              alt=""
              aria-hidden="true"
            >
            <span v-if="leader.mine" class="mine-badge">我</span>
            <b>{{ leader.amount }}</b>
          </div>
          <div v-if="!leaders.length" class="panel-empty">暂无榜单数据</div>
        </div>
        <button type="button" @click="$emit('leaderboard')">
          查看完整榜单 ›
        </button>
        </article>

        <article class="up-card risk-card">
          <div class="strategy-table__heading">
            <h2>机器人健康与风险</h2>
            <button type="button" @click="$emit('navigate', 'robots')">查看策略 ›</button>
          </div>
          <div class="risk-card__row good">
            <span><i />运行良好</span>
            <strong>{{ runningCount }} <small>个</small></strong>
          </div>
          <div class="risk-card__row warning">
            <span><i />需要关注</span>
            <div class="risk-card__row-actions">
              <strong>{{ attentionStrategyCount }} <small>个</small></strong>
              <button type="button" @click="$emit('navigate', 'robots')">查看 ›</button>
            </div>
          </div>
          <div class="risk-card__row danger">
            <span><i />高风险</span>
            <strong>{{ highRiskStrategyCountText }} <small>个</small></strong>
          </div>
          <div class="risk-card__health">
            <span>机器人健康评分 <b>{{ averageHealthText }}</b></span>
            <i><b :style="{ width: averageHealth === null ? '0%' : `${averageHealth}%` }" /></i>
          </div>
        </article>
      </div>
    </div>

    <div class="overview-services">
      <article class="up-card overview-service-card point-card">
        <div>
          <span class="overview-service-card__label">点卡余额</span>
          <strong class="point-card__balance">
            {{ pointCardBalance }}
            <small>点</small>
          </strong>
          <p>用于抵扣机器人策略服务费</p>
        </div>
        <div class="overview-service-card__actions">
          <button class="primary" type="button" @click="$emit('action', 'buy-card')">购买点卡</button>
          <button type="button" @click="$emit('navigate', 'bills', { category: 'fee' })">消费记录</button>
        </div>
      </article>

      <article class="up-card overview-service-card">
        <div>
          <h2>帮助中心</h2>
          <p>常见问题、费用与分成说明、风控规则与安全指南。</p>
        </div>
        <button class="overview-service-card__full-button" type="button" @click="$emit('support', 'help')">
          打开帮助中心
        </button>
      </article>

      <article class="up-card overview-service-card">
        <div>
          <h2>新手教程</h2>
          <p>三步完成首个机器人配置：充值 → 划转燃料 → 选择策略模板。</p>
        </div>
        <button class="overview-service-card__full-button" type="button" @click="$emit('support', 'tutorial')">
          查看教程
        </button>
      </article>
    </div>
  </section>
</template>

<script>
import EChartLine from '@/components/rebuild/EChartLine.vue'
import { robotNeedsMaximumRestart } from '@/api/robot-actions'
import { languageIntlLocale } from '@/utils/locale'
import {
  cumulativePoints,
  finiteNumber,
  firstNumber,
  firstText,
  formatNumber,
  formatRate,
  formatSigned
} from '@/utils/portal-data'

function uniqueRiskExceptionCount(exceptions) {
  const keys = new Set()
  exceptions.forEach((item, index) => {
    const strategyKey = firstText(item, [
      'strategy_uid',
      'strategy_id',
      'strategyId',
      'sid',
      'uid',
      'uid_one',
      'uid_two',
      'group_id',
      'id'
    ])
    keys.add(strategyKey || `exception-${index}`)
  })
  return keys.size
}

function shortDate(value) {
  const text = String(value === null || value === undefined ? '' : value).trim()
  const fullDate = text.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/)
  if (fullDate) {
    return `${String(fullDate[2]).padStart(2, '0')}-${String(fullDate[3]).padStart(2, '0')}`
  }
  const monthDay = text.match(/^(\d{1,2})[-/](\d{1,2})$/)
  if (monthDay) {
    return `${String(monthDay[1]).padStart(2, '0')}-${String(monthDay[2]).padStart(2, '0')}`
  }
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return [
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
  ].join('-')
}

export default {
  name: 'OverviewPanel',
  components: { EChartLine },
  props: {
    userName: { type: String, default: '--' },
    robots: { type: Array, required: true },
    balances: { type: Object, required: true },
    earnings: { type: Object, required: true },
    period: {
      type: Object,
      default: () => {
        const now = new Date()
        return { year: now.getFullYear(), month: now.getMonth() + 1 }
      }
    },
    periodLoading: { type: Boolean, default: false },
    registrationTime: { type: [String, Number], default: '' },
    leaders: { type: Array, default: () => [] },
    robotRisk: { type: Object, default: null },
    rate: { type: Number, default: null },
    lastSync: { type: Date, default: null },
    loading: { type: Boolean, default: false },
    errors: { type: Array, default: () => [] }
  },
  computed: {
    yearOptions() {
      const currentYear = new Date().getFullYear()
      const selectedYear = Number(this.period.year)
      const registrationYear = this.extractYear(this.registrationTime)
      const dataYears = this.periodRows
        .map(item => this.extractYear(firstText(item, [
          'profit_day',
          'date',
          'profit_month',
          'month',
          'create_time',
          'add_time'
        ])))
        .filter(year => year !== null)
      const dataStartYear = dataYears.length ? Math.min(...dataYears) : null
      let startYear = registrationYear !== null ? registrationYear : dataStartYear
      if (startYear === null || startYear > currentYear) startYear = currentYear
      if (Number.isInteger(selectedYear) && selectedYear > 0 && selectedYear < startYear) {
        startYear = selectedYear
      }
      const years = []
      for (let year = currentYear; year >= startYear; year -= 1) years.push(year)
      return years
    },
    monthOptions() {
      const now = new Date()
      const selectedYear = Number(this.period.year)
      return Array.from({ length: 12 }, (_, index) => ({
        value: index + 1,
        disabled: selectedYear === now.getFullYear() && index + 1 > now.getMonth() + 1
      }))
    },
    greeting() {
      const hour = new Date().getHours()
      if (hour < 6) return this.$t('凌晨好')
      if (hour < 12) return this.$t('早上好')
      if (hour < 18) return this.$t('下午好')
      return this.$t('晚上好')
    },
    syncTime() {
      if (!this.lastSync) return this.loading ? this.$t('正在加载') : this.$t('尚未同步')
      return new Intl.DateTimeFormat(languageIntlLocale(), {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).format(this.lastSync).replace(/\//g, '-')
    },
    runningCount() {
      return this.robots.filter(robot => robot.status === 'running').length
    },
    visibleLeaders() {
      return this.leaders.slice(0, 5)
    },
    weekRange() {
      const today = new Date()
      const weekday = today.getDay() || 7
      const monday = new Date(today)
      monday.setDate(today.getDate() - weekday + 1)
      return `${shortDate(monday)} ~ ${shortDate(today)}`
    },
    syncStatus() {
      if (this.loading) return this.$t('数据同步中')
      if (this.errors.length) return this.$t('部分数据暂不可用')
      return this.$t('数据已同步')
    },
    periodRows() {
      return this.earnings.monthRows || []
    },
    chartPoints() {
      return cumulativePoints(this.periodRows)
    },
    chartLabels() {
      return this.periodRows.map(item => {
        const value = firstText(item, ['profit_day', 'date', 'profit_month', 'month'])
        return shortDate(value) || value || '--'
      })
    },
    chartRates() {
      return this.periodRows.map(item =>
        firstNumber(item, ['month_yield', 'day_yield', 'yield', 'profit_yield'])
      )
    },
    axisLabels() {
      if (this.chartLabels.length <= 7) return this.chartLabels
      const indexes = [0, 1, 2, 3, 4, 5, 6].map(index =>
        Math.round(index * (this.chartLabels.length - 1) / 6)
      )
      return indexes.map(index => this.chartLabels[index])
    },
    periodAmount() {
      if (!this.chartPoints.length) return '--'
      return formatSigned(this.chartPoints[this.chartPoints.length - 1])
    },
    periodAmountClass() {
      if (!this.chartPoints.length) return ''
      return this.chartPoints[this.chartPoints.length - 1] < 0 ? 'negative' : 'positive'
    },
    periodRate() {
      const last = this.periodRows[this.periodRows.length - 1] || {}
      return formatRate(firstNumber(last, ['month_yield', 'day_yield']), { fraction: true })
    },
    convertedTotal() {
      const total = finiteNumber(this.balances.total)
      if (total === null || this.rate === null) return '--'
      return formatNumber(total * this.rate)
    },
    pointCardBalance() {
      return formatNumber(this.balances.card, 0)
    },
    riskScore() {
      return finiteNumber(this.robotRisk && this.robotRisk.scores)
    },
    riskExceptions() {
      if (!this.robotRisk || !Array.isArray(this.robotRisk.exceptions)) return []
      return this.robotRisk.exceptions.filter(item => Number(item && item.errorType) !== 5)
    },
    riskDataAvailable() {
      return Boolean(this.robotRisk) &&
        (this.riskScore !== null || Array.isArray(this.robotRisk.exceptions))
    },
    maximumPositionAttentionRobots() {
      return this.robots.filter(robot =>
        robot.status === 'paused' && robotNeedsMaximumRestart(robot)
      )
    },
    attentionStrategyCount() {
      return this.maximumPositionAttentionRobots.length
    },
    highRiskStrategyCount() {
      if (!this.riskDataAvailable) return null
      const interfaceHighRiskCount = uniqueRiskExceptionCount(
        this.riskExceptions.filter(item => Number(item && item.level) === 2)
      )
      const containXFallback = (
        Number(this.robotRisk && this.robotRisk.containX) === 1 &&
        this.riskScore !== 100
      ) ? 1 : 0
      return Math.min(
        this.robots.length,
        Math.max(interfaceHighRiskCount, containXFallback)
      )
    },
    highRiskStrategyCountText() {
      return this.highRiskStrategyCount === null ? '--' : this.highRiskStrategyCount
    },
    averageHealth() {
      return this.riskScore
    },
    averageHealthText() {
      return this.averageHealth === null ? '--' : `${formatNumber(this.averageHealth, 0)}%`
    },
    healthSummary() {
      const highRisk = this.highRiskStrategyCount === null ? '--' : this.highRiskStrategyCount
      return this.$t(`${this.runningCount} 良好 · ${this.attentionStrategyCount} 关注 · ${highRisk} 高风险`)
    }
  },
  methods: {
    extractYear(value) {
      if (value === null || value === undefined || value === '') return null
      const matched = String(value).match(/(?:^|[^0-9])((?:19|20)[0-9]{2})(?:[^0-9]|$)/)
      if (!matched) return null
      const year = Number(matched[1])
      return Number.isInteger(year) ? year : null
    },
    money(value) {
      return formatNumber(value)
    },
    signed(value) {
      return formatSigned(value)
    },
    rateText(value, fraction) {
      return formatRate(value, { fraction })
    },
    changeYear(event) {
      const year = Number(event.target.value)
      const now = new Date()
      const maximumMonth = year === now.getFullYear() ? now.getMonth() + 1 : 12
      const month = Math.min(Number(this.period.month) || 1, maximumMonth)
      this.$emit('period-change', { year, month })
    },
    changeMonth(event) {
      this.$emit('period-change', {
        year: Number(this.period.year),
        month: Number(event.target.value)
      })
    }
  }
}
</script>

<style scoped>
.overview-panel { display: flex; flex-direction: column; gap: 20px; }
.overview-banner { display: flex; min-height: 104px; align-items: center; justify-content: space-between; gap: 20px; padding: 24px 30px; color: #fff; border-radius: 16px; background: radial-gradient(760px 340px at 90% -40%, rgba(74,140,255,.42), transparent 62%), radial-gradient(560px 280px at -6% 140%, rgba(31,107,255,.32), transparent 60%), linear-gradient(130deg,#081737,#123289 58%,#1d5bd8); box-shadow: 0 16px 40px rgba(8,23,55,.2); }
.overview-banner__identity { display: flex; align-items: center; gap: 10px; }
.overview-banner h1 { margin: 0; font-size: 21px; letter-spacing: .3px; }
.pro-badge { padding: 2px 9px; color: #7a5200; border-radius: 999px; background: linear-gradient(135deg,#f9e7b3,#efc65c); font-size: 10.5px; font-weight: 800; letter-spacing: .8px; }
.risk-status { display: inline-flex; align-items: center; gap: 6px; padding: 3px 11px; color: rgba(255,255,255,.85); border-radius: 999px; background: rgba(255,255,255,.12); font-size: 11.5px; font-weight: 700; }
.risk-status i, .weekly-board__head > span i { width: 6px; height: 6px; border-radius: 50%; background: #9fe8c3; }
.overview-banner p { margin: 8px 0 0; color: rgba(255,255,255,.6); font-size: 12.5px; }
.overview-banner__right { display: flex; align-items: center; gap: 20px; }
.overview-banner__today { text-align: right; }
.overview-banner__today span { color: rgba(255,255,255,.65); font-size: 11.5px; }
.overview-banner__today span i { display: inline-block; width: 5px; height: 5px; border-radius: 50%; background: #12b76a; }
.overview-banner__today strong { display: block; margin-top: 3px; color: #8df5bf; font-size: 26px; }
.overview-banner__divider { width: 1px; height: 40px; background: rgba(255,255,255,.16); }
.overview-banner__actions { display: flex; gap: 9px; }
.overview-banner__actions button { padding: 10px 16px; color: #fff; border: 1px solid rgba(255,255,255,.28); border-radius: 9px; background: rgba(255,255,255,.12); font-size: 12px; font-weight: 700; white-space: nowrap; }
.overview-banner__actions .running { color: #8df5bf; border-color: rgba(125,240,174,.45); background: rgba(18,183,106,.16); }
.overview-stats { display: grid; grid-template-columns: repeat(4, 1fr); overflow: hidden; }
.overview-stats > div { padding: 22px 26px; }
.overview-stats > div + div { border-left: 1px solid #f0f2f6; }
.overview-stats span, .overview-stats small { display: block; color: #79808f; font-size: 12px; }
.overview-stats strong { display: block; margin-top: 7px; font-size: 27px; font-variant-numeric: tabular-nums; }
.overview-stats strong i { color: #8a90a0; font-size: 13px; font-style: normal; }
.overview-stats small { margin-top: 5px; color: #8a90a0; }
.overview-stats small b { padding: 2px 8px; color: #0b7a47; border-radius: 999px; background: #e8f6ee; }
.overview-stats small em { color: #0b9b57; font-style: normal; font-weight: 700; }
.positive { color: #0b9b57 !important; }
.negative { color: #e5484d !important; }
.overview-dashboard-grid { display: grid; grid-template-columns: minmax(0, 1fr) 400px; gap: 20px; align-items: stretch; }
.overview-dashboard__main, .overview-dashboard__side { display: grid; min-width: 0; grid-template-rows: 438px 458px; gap: 20px; }
.earnings-chart { display: flex; height: 438px; box-sizing: border-box; flex-direction: column; padding: 20px 22px 12px; }
.panel-heading, .strategy-table__heading, .weekly-board__head { display: flex; align-items: center; justify-content: space-between; }
.panel-heading h2, .weekly-board h2, .strategy-table h2, .risk-card h2 { margin: 0; font-size: 16px; font-weight: 800; line-height: 24px; }
.earnings-period-selects { display: flex; align-items: center; gap: 8px; }
.earnings-period-selects select { height: 34px; min-width: 88px; padding: 0 28px 0 12px; color: #4f5768; border: 1px solid #dce2ec; border-radius: 8px; outline: none; background-color: #fff; font-size: 12px; font-weight: 600; cursor: pointer; }
.earnings-period-selects select:last-child { min-width: 72px; }
.earnings-period-selects select:hover, .earnings-period-selects select:focus { color: #276bff; border-color: #8cb2ff; }
.earnings-period-selects select:disabled { color: #98a0af; cursor: wait; background-color: #f5f6f9; }
.earnings-chart__numbers { display: flex; gap: 36px; margin: 16px 0 6px; }
.earnings-chart__numbers span { display: block; color: #8a90a0; font-size: 12.5px; line-height: 18px; }
.earnings-chart__numbers strong { display: block; margin-top: 2px; color: #1b1f2a; font-size: 26px; font-weight: 800; line-height: 34px; font-variant-numeric: tabular-nums; }
.earnings-chart__canvas { min-height: 0; flex: 1; overflow: hidden; }
.earnings-chart__canvas .echarts-line { width: 100%; height: 100%; }
.panel-empty { display: grid; min-height: 120px; place-items: center; color: #98a0af; font-size: 12px; }
.earnings-chart__axis { display: flex; padding: 6px 2px; justify-content: space-between; color: #98a0af; font-size: 10px; }
.weekly-board { position: relative; display: flex; height: 438px; min-height: 438px; box-sizing: border-box; overflow: hidden; flex-direction: column; padding: 20px 22px; border: 1px solid #c7dbfc; border-radius: 14px; background: linear-gradient(165deg, #f5f9ff, #fff 40%); box-shadow: 0 4px 14px rgba(31,107,255,.1); }
.weekly-board::before { position: absolute; top: -52px; right: -46px; width: 150px; height: 150px; border-radius: 50%; background: rgba(31,107,255,.05); content: ''; pointer-events: none; }
.weekly-board__head, .weekly-board__list, .weekly-board > button { position: relative; z-index: 1; }
.weekly-board__head { margin-bottom: 8px; }
.weekly-board__head > div { display: flex; align-items: center; gap: 9px; }
.weekly-board__crown { display: grid; width: 28px; height: 28px; flex: 0 0 28px; place-items: center; color: #fff; border-radius: 9px; background: #1f6bff; }
.weekly-board__crown img { display: block; width: 15px; height: 15px; object-fit: contain; }
.weekly-board__head > span { display: flex; align-items: center; gap: 6px; padding: 3px 10px; color: #1f6bff; border-radius: 999px; background: #ebf1fe; font-size: 11px; font-weight: 800; line-height: 16px; }
.weekly-board__head > span i { width: 6px; height: 6px; flex: 0 0 6px; border-radius: 50%; background: #8cb7ff; }
.weekly-board__list { min-height: 0; flex: 1; margin: 0 0 12px; overflow-y: auto; }
.weekly-board__row { display: flex; min-height: 48px; box-sizing: border-box; align-items: center; gap: 11px; padding: 10px; border-radius: 10px; }
.weekly-board__row.mine { background: #f0f5fe; }
.weekly-board__row .rank { display: grid; width: 28px; height: 28px; flex: 0 0 28px; place-items: center; color: #667085; border-radius: 50%; background: #f1f3f6; font-size: 13px; font-weight: 800; font-variant-numeric: tabular-nums; }
.weekly-board__row.mine .rank { color: #276bff; background: #e3edff; }
.weekly-board__row .rank-1 { color: #b7791f; background: #fff0c6; }
.weekly-board__row .rank-2 { color: #667085; background: #eef1f5; }
.weekly-board__row .rank-3 { color: #b45309; background: #ffeadc; }
.weekly-board__row strong { overflow: hidden; color: #3a3f4e; font-size: 14px; font-weight: 700; line-height: 20px; text-overflow: ellipsis; white-space: nowrap; font-variant-numeric: tabular-nums; }
.weekly-board__row .crown { display: block; width: 15px; height: 15px; flex: 0 0 15px; object-fit: contain; }
.mine-badge { padding: 2px 9px; color: #fff; border-radius: 999px; background: #1f6bff; font-size: 11px; font-weight: 800; line-height: 16px; }
.weekly-board__row b { margin-left: auto; color: #0b9b57; font-size: 14px; font-weight: 800; line-height: 20px; white-space: nowrap; font-variant-numeric: tabular-nums; }
.weekly-board > button { min-height: 40px; margin-top: auto; padding: 11px 0; color: #fff; border: 0; border-radius: 9px; background: #1f6bff; font-size: 13.5px; font-weight: 700; line-height: 18px; }
.weekly-board > button:hover { background: #1558dc; }
.strategy-table, .risk-card { height: 458px; box-sizing: border-box; overflow: hidden; }
.risk-card { overflow-y: auto; }
.strategy-table { display: flex; flex-direction: column; }
.strategy-table > .strategy-table__heading { min-height: 56px; padding: 0 24px; }
.strategy-table__heading h2 { font-size: 16px; font-weight: 800; line-height: 24px; }
.strategy-table__heading button { padding: 0; color: #1f6bff; border: 0; background: transparent; font-size: 12px; font-weight: 700; line-height: 18px; }
.strategy-table__head, .strategy-table__row { display: grid; grid-template-columns: 2.95fr 1.49fr 1.25fr 1.64fr 1.95fr 1.41fr 54px; align-items: center; gap: 8px; padding: 0 24px; }
.strategy-table__head { min-height: 34px; color: #79808f; border-bottom: 1px solid #edf0f5; font-size: 12px; font-weight: 500; line-height: 18px; }
.strategy-table__head > span:nth-child(6), .strategy-table__row > :nth-child(6) { display: flex; width: 100%; height: 100%; box-sizing: border-box; align-items: center; justify-content: center; justify-self: stretch; text-align: center; }
.strategy-table__head > span:last-child { text-align: center; }
.strategy-table__scroll { min-height: 0; flex: 1; margin-right: 10px; overflow-x: hidden; overflow-y: auto; scrollbar-gutter: stable; }
.strategy-table__row { min-height: 61px; margin-right: -20px; color: #525b70; border-bottom: 1px solid #edf0f5; font-size: 14px; font-weight: 400; line-height: 20px; font-variant-numeric: tabular-nums; }
.strategy-table__row > strong:first-child { overflow: hidden; color: #111827; font-weight: 700; text-overflow: ellipsis; white-space: nowrap; }
.strategy-table__row .health { display: flex; align-items: center; gap: 7px; }
.strategy-table__row .health > i { width: 52px; height: 6px; overflow: hidden; border-radius: 99px; background: #eef0f5; }
.strategy-table__row .health b { display: block; height: 100%; border-radius: inherit; background: #12b76a; }
.strategy-table__row .risk { min-width: 34px; padding: 2px 9px; justify-self: center; color: #0b7a47; border-radius: 999px; background: #e8f6ee; text-align: center; font-size: 12px; font-weight: 700; line-height: 20px; }
.strategy-table__row .risk.warning { color: #b45309; background: #fbf1df; }
.strategy-table__row > button { width: 54px; min-height: 32px; padding: 0 8px; justify-self: center; color: #1f6bff; border: 0; border-radius: 8px; background: #ebf1fe; font-size: 12px; font-weight: 700; line-height: 18px; }
.risk-card { display: flex; flex-direction: column; gap: 10px; padding: 18px; }
.risk-card .strategy-table__heading { padding: 0 0 3px; }
.risk-card__row { display: flex; padding: 13px 14px; align-items: center; justify-content: space-between; border-radius: 10px; }
.risk-card__row > span { display: flex; align-items: center; gap: 8px; font-size: 12px; font-weight: 600; }
.risk-card__row > span i { width: 7px; height: 7px; border-radius: 50%; }
.risk-card__row strong { font-size: 14px; }
.risk-card__row small { font-size: 10px; }
.risk-card__row-actions { display: flex; align-items: center; gap: 8px; }
.risk-card__row-actions button { padding: 0; color: currentColor; border: 0; background: transparent; font-size: 11px; font-weight: 700; cursor: pointer; }
.risk-card__row-actions button:hover { text-decoration: underline; }
.risk-card__row.good { color: #0b7a47; background: #f0faf4; }.risk-card__row.good i { background: #12b76a; }
.risk-card__row.warning { color: #b45309; background: #fbf4e3; }.risk-card__row.warning i { background: #e8a23d; }
.risk-card__row.danger { color: #d92d20; background: #fcefed; }.risk-card__row.danger i { background: #e5484d; }
.risk-card__health > span { display: flex; margin-bottom: 7px; justify-content: space-between; color: #79808F; font-size: 13px; }
.risk-card__health > i { display: block; height: 7px; overflow: hidden; border-radius: 99px; background: #eef0f5; }
.risk-card__health > i b { display: block; width: 0; height: 100%; border-radius: inherit; background: #1f6bff; }
.risk-card > small { margin-top: auto; padding-top: 10px; color: #98a0af; border-top: 1px solid #f3f4f8; font-size: 10px; }
.weekly-board__list, .strategy-table__scroll, .risk-card { scrollbar-color: #cbd3e1 transparent; scrollbar-width: thin; }
.weekly-board__list::-webkit-scrollbar, .strategy-table__scroll::-webkit-scrollbar, .risk-card::-webkit-scrollbar { width: 5px; }
.weekly-board__list::-webkit-scrollbar-button, .strategy-table__scroll::-webkit-scrollbar-button, .risk-card::-webkit-scrollbar-button { display: none; width: 0; height: 0; }
.weekly-board__list::-webkit-scrollbar-thumb, .strategy-table__scroll::-webkit-scrollbar-thumb, .risk-card::-webkit-scrollbar-thumb { border-radius: 99px; background: #cbd3e1; }
.overview-services { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; }
.overview-service-card { display: flex; min-height: 146px; flex-direction: column; justify-content: space-between; padding: 22px; }
.overview-service-card h2 { margin: 0; font-size: 16px; }
.overview-service-card p { margin: 7px 0 18px; color: #7d8494; font-size: 11.5px; line-height: 1.6; }
.overview-service-card__label { color: #79808f; font-size: 12px; }
.point-card__balance { display: block; margin-top: 8px; font-size: 27px; font-variant-numeric: tabular-nums; }
.point-card__balance small { margin-left: 4px; color: #8a90a0; font-size: 11px; }
.overview-service-card__actions { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.overview-service-card__actions button, .overview-service-card__full-button { min-height: 38px; color: #252a36; border: 1px solid #dce1eb; border-radius: 8px; background: #fff; font-size: 11.5px; font-weight: 700; }
.overview-service-card__actions button.primary { color: #fff; border-color: #1f6bff; background: #1f6bff; }
.overview-service-card__actions button:hover, .overview-service-card__full-button:hover { color: #1f6bff; border-color: #9dbbff; background: #f5f8ff; }
.overview-service-card__actions button.primary:hover { color: #fff; border-color: #1558dc; background: #1558dc; }
@media (max-width: 1160px) { .overview-banner { align-items: flex-start; flex-direction: column; } .overview-dashboard-grid { grid-template-columns: 1fr; } .overview-dashboard__main, .overview-dashboard__side { grid-template-rows: auto; } .overview-services { grid-template-columns: 1fr; } .overview-stats { grid-template-columns: repeat(2,1fr); } }
@media (max-width: 720px) {
  .overview-banner__right,
  .overview-banner__actions { align-items: flex-start; flex-direction: column; }
  .overview-banner__today { text-align: left; }
  .overview-banner__divider { display: none; }
  .overview-stats { grid-template-columns: 1fr; }
  .overview-stats > div + div { border-top: 1px solid #f0f2f6; border-left: 0; }
  .strategy-table__head { display: none; }
  .strategy-table__scroll {
    width: 100%;
    max-width: 100%;
    margin-right: 0;
    overflow-x: hidden;
    overscroll-behavior-x: none;
    touch-action: pan-y;
  }
  .strategy-table__row {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    margin-right: 0;
    grid-template-columns: minmax(0, 1fr) 32px 96px;
  }
  .strategy-table__row > :nth-child(2),
  .strategy-table__row > :nth-child(3),
  .strategy-table__row > :nth-child(4),
  .strategy-table__row > :nth-child(6) { display: none; }
  .strategy-table__row > button {
    width: 96px;
    max-width: 100%;
    box-sizing: border-box;
    white-space: nowrap;
  }
}
</style>
